import { CONSULT_REPORT_TYPES } from '@dermalink/shared';
import type { ConsultReportCell, ConsultReportType, OrgAdminConsultReportResponse } from '@dermalink/shared';

export const CONSULT_TYPE_LABEL: Record<ConsultReportType, string> = {
  new_consult:  'Nieuw consult',
  prescription: 'Enkel voorschrift',
  follow_up:    'Opvolgconsult',
};

/** Follow-ups are free, so they get a count but no revenue column. */
export function hasRevenue(type: ConsultReportType): boolean {
  return type !== 'follow_up';
}

export interface ReportLine {
  label: string;
  cells: Record<ConsultReportType, ConsultReportCell>;
  total: ConsultReportCell;
}

export interface ReportGroup {
  doctorName: string;
  subtotal:   ReportLine;
  months:     ReportLine[];
}

export interface ConsultReport {
  groups: ReportGroup[];
  /** Practice-wide sum; null when filtered to one doctor, whose subtotal already is the total. */
  total:  ReportLine | null;
}

const MONTH_NAMES = ['januari', 'februari', 'maart', 'april', 'mei', 'juni', 'juli', 'augustus', 'september', 'oktober', 'november', 'december'];

export function doctorDisplayName(name: string): string {
  return `Dr. ${name}`;
}

function emptyCells(): Record<ConsultReportType, ConsultReportCell> {
  return Object.fromEntries(CONSULT_REPORT_TYPES.map((t) => [t, { count: 0, revenue_cents: 0, platform_fee_cents: 0 }])) as Record<ConsultReportType, ConsultReportCell>;
}

function line(label: string, cells: Record<ConsultReportType, ConsultReportCell>): ReportLine {
  const total = { count: 0, revenue_cents: 0, platform_fee_cents: 0 };
  for (const t of CONSULT_REPORT_TYPES) {
    total.count              += cells[t].count;
    total.revenue_cents      += cells[t].revenue_cents;
    total.platform_fee_cents += cells[t].platform_fee_cents;
  }
  return { label, cells, total };
}

function sumLines(label: string, lines: ReportLine[]): ReportLine {
  const cells = emptyCells();
  for (const l of lines) {
    for (const t of CONSULT_REPORT_TYPES) {
      cells[t].count              += l.cells[t].count;
      cells[t].revenue_cents      += l.cells[t].revenue_cents;
      cells[t].platform_fee_cents += l.cells[t].platform_fee_cents;
    }
  }
  return line(label, cells);
}

/**
 * Turns the API's rows into the grid: per doctor a subtotal plus one line per month with
 * consults, in calendar order. Empty months and doctors without consults that year are left out.
 */
export function buildConsultReport(data: OrgAdminConsultReportResponse, doctorSub: string | null): ConsultReport {
  const byKey   = new Map(data.rows.map((r) => [`${r.doctor_cognito_sub}|${r.month}`, r.cells]));
  const doctors = doctorSub ? data.doctors.filter((d) => d.cognito_sub === doctorSub) : data.doctors;

  const groups = doctors.flatMap((d) => {
    const months = MONTH_NAMES.flatMap((name, i) => {
      const cells = byKey.get(`${d.cognito_sub}|${data.year}-${String(i + 1).padStart(2, '0')}`);
      return cells ? [line(name, cells)] : [];
    });
    const subtotal = sumLines('Totaal', months);
    return subtotal.total.count ? [{ doctorName: doctorDisplayName(d.name), subtotal, months }] : [];
  });

  return { groups, total: doctorSub ? null : sumLines('Totaal praktijk', groups.map((g) => g.subtotal)) };
}

// Belgian Excel expects ';' between fields and ',' as decimal separator; the BOM makes it read UTF-8.
function csvField(value: string): string {
  return /[";\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

function csvAmount(cents: number): string {
  return (cents / 100).toFixed(2).replace('.', ',');
}

/** The grid as CSV: the same lines in the same order, with the year spelled out in each month. */
export function consultReportCsv(report: ConsultReport, year: number): string {
  const header = ['Arts', 'Maand'];
  for (const t of CONSULT_REPORT_TYPES) {
    header.push(`${CONSULT_TYPE_LABEL[t]} - aantal`);
    if (hasRevenue(t)) header.push(`${CONSULT_TYPE_LABEL[t]} - omzet (EUR)`);
  }
  header.push('Totaal - aantal', 'Totaal - omzet (EUR)', 'Totaal - platformvergoeding incl. btw (EUR)', 'Totaal - netto (EUR)');

  const row = (doctor: string, period: string, l: ReportLine) => [
    doctor,
    period,
    ...CONSULT_REPORT_TYPES.flatMap((t) => hasRevenue(t) ? [String(l.cells[t].count), csvAmount(l.cells[t].revenue_cents)] : [String(l.cells[t].count)]),
    String(l.total.count),
    csvAmount(l.total.revenue_cents),
    csvAmount(l.total.platform_fee_cents),
    csvAmount(l.total.revenue_cents - l.total.platform_fee_cents),
  ];

  const lines = [header];
  for (const g of report.groups) {
    lines.push(row(g.doctorName, `${g.subtotal.label} ${year}`, g.subtotal));
    for (const m of g.months) lines.push(row(g.doctorName, `${m.label} ${year}`, m));
  }
  if (report.total) lines.push(row(report.total.label, String(year), report.total));

  return '﻿' + lines.map((l) => l.map(csvField).join(';')).join('\r\n');
}
